import { useState } from 'react';
import { Zap, Plus, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import QuickTopUpModal from './QuickTopUpModal';
import { useNavigate } from 'react-router-dom';

const CreditBadge = ({ credits, darkMode }) => {
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const navigate = useNavigate();
  
  const getCreditColor = () => {
    if (credits === 0) return 'red';
    if (credits <= 10) return 'orange';
    if (credits <= 30) return 'yellow';
    return 'green';
  };
  
  const color = getCreditColor();
  
  const colorClasses = {
    red: {
      icon: 'text-red-500 dark:text-red-400',
      text: 'text-red-600 dark:text-red-400',
      bg: 'bg-red-50 dark:bg-red-900/20',
      border: 'border-red-200 dark:border-red-800',
      dropdownText: 'text-red-700'
    },
    orange: {
      icon: 'text-orange-500 dark:text-orange-400',
      text: 'text-orange-600 dark:text-orange-400',
      bg: 'bg-orange-50 dark:bg-orange-900/20',
      border: 'border-orange-200 dark:border-orange-800',
      dropdownText: 'text-orange-700'
    },
    yellow: {
      icon: 'text-yellow-600 dark:text-yellow-400',
      text: 'text-yellow-700 dark:text-yellow-400',
      bg: 'bg-yellow-50 dark:bg-yellow-900/20',
      border: 'border-yellow-200 dark:border-yellow-800',
      dropdownText: 'text-yellow-700'
    },
    green: {
      icon: 'text-green-600 dark:text-green-400',
      text: 'text-green-600 dark:text-green-400',
      bg: 'bg-green-50 dark:bg-green-900/20',
      border: 'border-green-200 dark:border-green-800',
      dropdownText: 'text-green-700'
    }
  };
  
  const classes = colorClasses[color];

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={`relative gap-1.5 px-2.5 py-1.5 h-auto transition-all duration-200 transform hover:scale-105 active:scale-95 ${darkMode ? 'hover:bg-gray-800 hover:shadow-lg hover:shadow-gray-700/50' : 'hover:bg-gray-100 hover:shadow-lg hover:shadow-gray-300/50'}`}
          >
            <Zap className={`w-3.5 h-3.5 ${classes.icon}`} fill="currentColor" />
            <span className={`text-sm font-semibold ${classes.text}`}>
              {credits}
            </span>
            {credits <= 10 && credits > 0 && (
              <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-orange-500 rounded-full" />
            )}
            {credits === 0 && (
              <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            )}
          </Button>
        </DropdownMenuTrigger>
        
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel>
            <div className="flex items-center justify-between">
              <span>Credit Balance</span>
              <span className={`font-bold text-lg ${classes.dropdownText}`}>{credits}</span>
            </div>
          </DropdownMenuLabel>
          
          <DropdownMenuSeparator />
          
          <div className="p-2">
            <div className={`p-3 rounded-lg ${classes.bg} ${classes.border} border mb-2`}>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                {credits === 0 && '⚠️ No credits remaining'}
                {credits > 0 && credits <= 10 && '⚠️ Running low on credits'}
                {credits > 10 && credits <= 30 && '✓ You have some credits'}
                {credits > 30 && '✓ You are all set!'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {credits === 0 && 'Top up now to continue analyzing'}
                {credits > 0 && credits <= 10 && 'Consider topping up soon'}
                {credits > 10 && credits <= 30 && 'Top up to avoid interruptions'}
                {credits > 30 && 'Plenty of credits for your analyses'}
              </p>
            </div>
          </div>
          
          <DropdownMenuItem onClick={() => setShowTopUpModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            <span>Quick Top-Up</span>
          </DropdownMenuItem>
          
          <DropdownMenuItem onClick={() => navigate('/pricing')}>
            <TrendingUp className="w-4 h-4 mr-2" />
            <span>View Plans</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <QuickTopUpModal
        open={showTopUpModal}
        onOpenChange={setShowTopUpModal}
        currentCredits={credits}
        onSuccess={() => {
          setShowTopUpModal(false);
        }}
      />
    </>
  );
};

export default CreditBadge;
